import { Label, QuantityControl } from '@/components/ui'
import { formatPrice } from '@/utils/formatting'

interface QuantityIngredient {
  id: string
  name: string
  price: number // display price per unit
  isEnabled: boolean
}

interface QuantityRowProps {
  rowTitle: string
  minSelections: number
  maxSelections: number
  maxQuantityPerItem: number | null // null = maxSelections
  ingredients: QuantityIngredient[]
  quantities: Record<string, number> // ingredientId -> qty
  onQuantityChange: (ingredientId: string, newQty: number) => void
  freeQuantity?: number
  isDynamic?: boolean
  pricingLabel?: string
}

export function QuantityRow({
  rowTitle,
  minSelections,
  maxSelections,
  maxQuantityPerItem,
  ingredients,
  quantities,
  onQuantityChange,
  freeQuantity = 0,
  isDynamic = false,
  pricingLabel,
}: QuantityRowProps) {
  const totalQty = Object.values(quantities).reduce((sum, q) => sum + q, 0)
  const perItemCap = maxQuantityPerItem ?? maxSelections

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-muted-foreground">
            {rowTitle}
            {minSelections > 0 && <span className="text-destructive ml-1">*</span>}
          </Label>
          {isDynamic && freeQuantity > 0 && (
            <span className="text-[11px] text-primary font-medium">
              ({Math.min(freeQuantity, totalQty)} of {freeQuantity} free)
            </span>
          )}
          {pricingLabel && (
            <span className="text-[11px] text-muted-foreground font-medium">
              · {pricingLabel}
            </span>
          )}
          {minSelections > 0 && totalQty < minSelections && (
            <span className="text-[11px] text-destructive font-medium">
              Select at least {minSelections}
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          ({totalQty}/{maxSelections})
        </span>
      </div>

      <div className="space-y-1.5">
        {ingredients.map((ing) => {
          const qty = quantities[ing.id] || 0
          const canIncrement = ing.isEnabled && totalQty < maxSelections && qty < perItemCap
          const canDecrement = qty > 0

          return (
            <div
              key={ing.id}
              className="flex items-center justify-between py-1.5 px-2 rounded-lg border border-border bg-background"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm truncate">{ing.name}</span>
                {ing.price > 0 && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    +{formatPrice(ing.price, undefined, false)}
                  </span>
                )}
                {isDynamic && ing.price === 0 && (
                  <span className="text-xs text-primary shrink-0">Included</span>
                )}
              </div>

              <QuantityControl
                value={qty}
                onDecrement={() => onQuantityChange(ing.id, qty - 1)}
                onIncrement={() => onQuantityChange(ing.id, qty + 1)}
                decrementDisabled={!canDecrement}
                incrementDisabled={!canIncrement}
                size="sm"
                variant="outline"
                className={
                  canDecrement || canIncrement
                    ? '[&_button]:border-foreground [&_button]:text-foreground [&_button:hover]:bg-muted'
                    : '[&_button]:border-border [&_button]:text-muted-foreground/40'
                }
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
